/**
 * Secure token storage for mobile platforms
 * Uses Capacitor Preferences (encrypted on iOS via Keychain)
 * On web, uses sessionStorage as fallback
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

export interface TokenData {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_at: number; // Unix timestamp in milliseconds
}

const TOKEN_KEY = 'cognito_tokens';

/**
 * Save tokens to secure storage
 */
export async function saveTokens(tokens: TokenData): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      // Native platform - use Capacitor Preferences (iOS Keychain)
      await Preferences.set({
        key: TOKEN_KEY,
        value: JSON.stringify(tokens),
      });
      console.log('[Token Storage] Tokens saved to native storage');
    } else {
      // Web fallback - use sessionStorage
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
      console.log('[Token Storage] Tokens saved to sessionStorage');
    }
  } catch (error) {
    console.error('[Token Storage] Error saving tokens:', error);
    throw error;
  }
}

/**
 * Get tokens from secure storage
 */
export async function getTokens(): Promise<TokenData | null> {
  try {
    console.log('[Token Storage] getTokens: START');
    
    let value: string | null = null;
    
    if (Capacitor.isNativePlatform()) {
      // Native platform - use Capacitor Preferences
      console.log('[Token Storage] getTokens: Calling Preferences.get...');
      const result = await Preferences.get({ key: TOKEN_KEY });
      console.log('[Token Storage] getTokens: Got result from Preferences');
      value = result.value;
    } else {
      // Web fallback - use sessionStorage
      console.log('[Token Storage] getTokens: Using sessionStorage');
      value = sessionStorage.getItem(TOKEN_KEY);
    }
    
    console.log('[Token Storage] getTokens: Value exists?', value ? 'yes' : 'no');
    
    if (!value) {
      console.log('[Token Storage] getTokens: No value, returning null');
      return null;
    }

    console.log('[Token Storage] getTokens: Parsing tokens...');
    const tokens = JSON.parse(value) as TokenData;
    console.log('[Token Storage] getTokens: Returning tokens');
    return tokens;
  } catch (error) {
    console.error('[Token Storage] Error reading tokens:', error);
    return null;
  }
}

/**
 * Clear all stored tokens
 */
export async function clearTokens(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      // Native platform - use Capacitor Preferences
      await Preferences.remove({ key: TOKEN_KEY });
    } else {
      // Web fallback - use sessionStorage
      sessionStorage.removeItem(TOKEN_KEY);
    }
    console.log('[Token Storage] Tokens cleared');
  } catch (error) {
    console.error('[Token Storage] Error clearing tokens:', error);
    throw error;
  }
}

/**
 * Check if we're on a native platform
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

